import { Button, Flex, Spinner, Text } from "@hackthenorth/north";
import React, { useEffect, useState } from "react";
import tw, { styled } from "twin.macro";

import Modal from "src/shared/components/Modal";
import PageWrapper from "src/shared/components/PageWrapper";
import { useHackerContext, useUserContext } from "src/shared/contexts";

import {
  formatLookupState,
  LOADING_RECOVERY_DELAY_MS,
} from "./loadingDiagnostics";

const AuthenticationLoadingModal: React.FC = () => {
  const [showRecovery, setShowRecovery] = useState(false);
  const {
    attendeeFound,
    email,
    error: userError,
    isAuthenticated,
    signInAgain,
    userFound,
  } = useUserContext();
  const { claimFound, error: claimError } = useHackerContext();

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setShowRecovery(true),
      LOADING_RECOVERY_DELAY_MS
    );
    return () => window.clearTimeout(timeout);
  }, []);

  return (
    <>
      <Modal
        isOpen
        width={showRecovery ? "560px" : undefined}
        title={showRecovery ? "This is taking longer than expected" : undefined}
        actions={
          showRecovery ? (
            <Button mods="primary" onClick={signInAgain}>
              Sign in again
            </Button>
          ) : undefined
        }
      >
        <LoadingContent expanded={showRecovery}>
          <Flex align="center" justify="center">
            <Spinner />
          </Flex>
          {showRecovery && (
            <RecoveryContent>
              <Text>
                It looks like something might be going wrong while we load your
                account. Try signing in again to reset your session.
              </Text>
              <Text>
                If this keeps happening, email{" "}
                <SupportLink href="mailto:hello@hackthenorth.com">
                  hello@hackthenorth.com
                </SupportLink>{" "}
                and include a screenshot of the diagnostic information below.
              </Text>
              <Diagnostics aria-label="Account loading diagnostics">
                <DiagnosticHeading>Diagnostic information</DiagnosticHeading>
                <DiagnosticRow>
                  <span>Email</span>
                  <span>{email ?? "Unavailable"}</span>
                </DiagnosticRow>
                <DiagnosticRow>
                  <span>Authentication</span>
                  <span>{isAuthenticated ? "Signed in" : "Not signed in"}</span>
                </DiagnosticRow>
                <DiagnosticRow>
                  <span>User profile</span>
                  <span>{formatLookupState(userFound)}</span>
                </DiagnosticRow>
                <DiagnosticRow>
                  <span>2026 attendee</span>
                  <span>{formatLookupState(attendeeFound)}</span>
                </DiagnosticRow>
                <DiagnosticRow>
                  <span>RSVP claim</span>
                  <span>{formatLookupState(claimFound)}</span>
                </DiagnosticRow>
                <DiagnosticRow>
                  <span>Lookup error</span>
                  <span>{userError || claimError ? "Detected" : "None"}</span>
                </DiagnosticRow>
              </Diagnostics>
            </RecoveryContent>
          )}
        </LoadingContent>
      </Modal>
      <PageWrapper pageTitle="" />
    </>
  );
};

const LoadingContent = styled.div<{ expanded: boolean }>`
  ${({ expanded }) => expanded && tw`flex flex-col gap-24`}
`;

const RecoveryContent = styled.div`
  ${tw`flex flex-col gap-16`}
  color: ${({ theme }) => theme.globalConstants.color.textDark};
`;

const SupportLink = styled.a`
  ${tw`underline`}
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
`;

const Diagnostics = styled.div`
  ${tw`p-16 rounded-8`}
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
`;

const DiagnosticHeading = styled.div`
  ${tw`font-bold mb-8`}
`;

const DiagnosticRow = styled.div`
  ${tw`flex justify-between gap-16 py-4`}

  span:last-child {
    ${tw`text-right font-bold break-all`}
  }
`;

export default AuthenticationLoadingModal;
